import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useAssetTableJson from "../../../../../json/useAssetTableJson";

import { CustomModal } from "../../../../../utils/UtilFunctions";
import { AddModalStyles } from "../../../../../styles/CustomModalStyles";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { useLazyQuery } from "@apollo/client";
import { GetAssetCountsReport } from "../../../../../queries/queries";
import {
  GetAssetCountsReportResult,
  GetAssetCountsReportVariables,
  tableDetails,
} from "../../../../../queries/types";
import { TableType } from "../../../customer/companies/queries/types";
import {
  TableContainer,
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
} from "@mui/material";
import useCompanyConfiguration from "../../../../../customHooks/useCompanyConfiguration";
import useLabels from "../../../../../customHooks/useLabels";
import { EMPTY_STRING } from "../../../../../utils/constants";
import TableTypeAutocomplete from "../../../../../customComponents/TableTypeAutocomplete";
import CompaniesAutocomplete from "../../../customer/companies/CompaniesAutocomplete";
import SubsidiarySelect from "../../../../../customComponents/SubsidiarySelect";
import SiteSelect from "../../../../../customComponents/SiteSelect";
import { Button } from "../../../../../stories/Button/Button";
import { useNavigate } from "react-router-dom";
import ItemDetails from "./modal/ItemDetails";
import { AppContext } from "../../../../../context/context";

interface rowsData {
  company: string;
  subsidiary: string;
  sub_site: string;
  company_span: number;
  subsidiary_span: number;
  sub_site_span: number;
  total_asset: number | string;
  working: number | string;
  not_working: number | string;
}

interface totalCount {
  totalAssetCount: number;
  workingAssetCount: number;
  notWorkingAssetCount: number;
}

interface Props {
  setDetailAssets?: React.Dispatch<React.SetStateAction<boolean>>;
}
const AssetSummaryTable = ({ setDetailAssets }: Props) => {
  const navigate = useNavigate();
  const [itemDetailsModal, setItemDetails] = useState(false);
  const { state } = useContext(AppContext);
  const { item_id, company_id, customer_id, sub_id, siteID } = useUrlParams();

  const { companyLabel, subsidiaryLabel, subsidiarySiteLabel } = useLabels();
  const [colSpan, setColSpan] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState(0);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState(0);
  const [selectedSite, setSelectedSite] = useState(0);

  const [selectedTableID, setSelectedTableID] = useState<tableDetails>({
    tableID: customer_id!,
    tableType: TableType.CUSTOMER,
  });

  const [selectedReturnType, setSelectedReturnType] =
    useState<TableType | null>(null);

  let idsSet = new Set();

  const { USE_ENABLE_SUBSIDIARY, USE_ENABLE_SUBSIDIARY_SITE } =
    useCompanyConfiguration();

  const [GetAssetDetails, { data }] = useLazyQuery<
    GetAssetCountsReportResult,
    GetAssetCountsReportVariables
  >(GetAssetCountsReport);

  useEffect(() => {
    if ((item_id || state.itmId) && customer_id) {
      GetAssetDetails({
        variables: {
          customerID: customer_id,
          itemID: item_id ? item_id : state.itmId,
          ...selectedTableID,
        },
      });
    }
  }, [item_id, GetAssetDetails, customer_id, selectedTableID, company_id]);

  const modifiedArrayForTable: rowsData[] = useMemo(() => {
    return data && data.GetAssetCountsReport
      ? data.GetAssetCountsReport.flatMap((comp) => {
          if (
            comp.subsidiaryDetails &&
            comp.subsidiaryDetails.length > 0 &&
            (selectedReturnType === TableType.SUBSIDIARY ||
              selectedReturnType === TableType.SITE ||
              selectedReturnType === null)
          ) {
            return comp.subsidiaryDetails.flatMap((sub) => {
              if (
                sub.siteDetails &&
                sub.siteDetails.length > 0 &&
                (selectedReturnType === TableType.SITE ||
                  selectedReturnType === null)
              ) {
                return sub.siteDetails.flatMap((site) => {
                  const getCountFrom = () => {
                    switch (selectedReturnType) {
                      // case TableType.COMPANY:
                      //   return {
                      //     total_asset: comp.assetCount,
                      //     working: comp.workingCount,
                      //     not_working: comp.notWorkingCount,
                      //   };
                      // case TableType.SUBSIDIARY:
                      //   return {
                      //     total_asset: sub.assetCount,
                      //     working: sub.workingCount,
                      //     not_working: sub.notWorkingCount,
                      //   };
                      case TableType.SITE:
                        return {
                          total_asset: site.assetCount,
                          working: site.workingCount,
                          not_working: site.notWorkingCount,
                        };
                      default:
                        return {
                          total_asset: site.assetCount,
                          working: site.workingCount,
                          not_working: site.notWorkingCount,
                        };
                    }
                  };

                  let result: rowsData = {
                    company: idsSet.has(comp.companyID)
                      ? EMPTY_STRING
                      : comp.companyName,
                    company_span: idsSet.has(comp.companyID)
                      ? 1
                      : comp.spanCount
                      ? comp.spanCount
                      : 1,
                    subsidiary: idsSet.has(sub.subsidiaryID)
                      ? EMPTY_STRING
                      : sub.subsidiaryName,
                    subsidiary_span: idsSet.has(sub.subsidiaryID)
                      ? 1
                      : sub.spanCount
                      ? sub.spanCount
                      : 1,
                    sub_site: idsSet.has(site.siteID)
                      ? EMPTY_STRING
                      : site.siteName,
                    sub_site_span: 1,
                    total_asset: getCountFrom().total_asset
                      ? getCountFrom().total_asset
                      : "-",
                    working: getCountFrom().working
                      ? getCountFrom().working
                      : "-",
                    not_working: getCountFrom().not_working
                      ? getCountFrom().not_working
                      : "-",
                  };

                  idsSet = new Set([
                    comp.companyID,
                    sub.subsidiaryID,
                    site.siteID,
                    ...idsSet,
                  ]);
                  return result;
                });
              } else {
                let result: rowsData = {
                  company: idsSet.has(comp.companyID)
                    ? EMPTY_STRING
                    : comp.companyName,
                  company_span: comp.subsidiaryDetails.length,
                  subsidiary: idsSet.has(sub.subsidiaryID)
                    ? EMPTY_STRING
                    : sub.subsidiaryName,
                  subsidiary_span:
                    selectedReturnType === TableType.SUBSIDIARY
                      ? 1
                      : sub.spanCount
                      ? sub.spanCount
                      : 1,
                  sub_site: EMPTY_STRING,
                  sub_site_span: 1,
                  total_asset: sub.assetCount,
                  working: sub.workingCount,
                  not_working: sub.notWorkingCount,
                };

                idsSet = new Set([comp.companyID, sub.subsidiaryID, ...idsSet]);
                return result;
              }
            });
          } else {
            let result: rowsData = {
              company: idsSet.has(comp.companyID)
                ? EMPTY_STRING
                : comp.companyName,
              company_span: 1,
              subsidiary: EMPTY_STRING,
              subsidiary_span: 1,
              sub_site: EMPTY_STRING,
              sub_site_span: 1,
              total_asset: comp.assetCount,
              working: comp.workingCount,
              not_working: comp.notWorkingCount,
            };
            idsSet = new Set([comp.companyID, ...idsSet]);
            return result;
          }
        })
      : [];
  }, [data, selectedReturnType]);

  const total: totalCount = useMemo(() => {
    return modifiedArrayForTable.reduce(
      (acc, item) => {
        acc.totalAssetCount +=
          item.total_asset === "-" ? 0 : Number(item.total_asset);
        acc.workingAssetCount +=
          item.working === "-" ? 0 : Number(item.working);
        acc.notWorkingAssetCount +=
          item.not_working === "-" ? 0 : Number(item.not_working);
        return acc;
      },
      {
        totalAssetCount: 0,
        workingAssetCount: 0,
        notWorkingAssetCount: 0,
      }
    );
  }, [modifiedArrayForTable]);

  const enableFields = useCallback(
    (tableType: TableType) => {
      switch (tableType) {
        case TableType.SUBSIDIARY:
          return (
            USE_ENABLE_SUBSIDIARY && selectedReturnType !== TableType.COMPANY
          );
        case TableType.SITE:
          return (
            USE_ENABLE_SUBSIDIARY_SITE &&
            selectedReturnType !== TableType.COMPANY &&
            selectedReturnType !== TableType.SUBSIDIARY
          );
        default:
          return true;
      }
    },
    [selectedReturnType, USE_ENABLE_SUBSIDIARY, USE_ENABLE_SUBSIDIARY_SITE]
  );

  useEffect(() => {
    if (company_id) {
      setSelectedCompany(company_id);
      setSelectedTableID({
        tableID: company_id,
        tableType: TableType.COMPANY,
      });
    }
    if (sub_id) {
      setSelectedSubsidiary(0);
      setSelectedTableID({
        tableID: sub_id,
        tableType: TableType.SUBSIDIARY,
      });
      setColSpan(2);
    }
    if (siteID) {
      setSelectedSite(0);
      setSelectedTableID({
        tableID: siteID,
        tableType: TableType.SITE,
      });
    }
    if (selectedReturnType === TableType.COMPANY) {
      setColSpan(1);
    } else if (selectedReturnType === TableType.SUBSIDIARY) {
      setColSpan(2);
    } else {
      setColSpan(3);
    }
  }, [selectedReturnType, company_id, sub_id, siteID]);

  return (
    <>
      <div className="ast-sum-report__table">
        {/* <div className="ast-sum-report__table--breadcrumbs">
          company/subsidiary/
        </div> */}
        <div className="row ast-sum-report__table--filters g-0">
          <div className="col-2">
            <TableTypeAutocomplete
              onChange={(newValue) => {
                if (newValue) {
                  setSelectedReturnType(newValue.value);
                } else {
                  setSelectedReturnType(null);
                }
              }}
              onKeyDown={() => {}}
              value={selectedReturnType}
              label="Table Type"
              disableOptions={[TableType.ASSET, TableType.JUNCTION]}
            />
          </div>
          {company_id === undefined && (
            <div className="col-2">
              <CompaniesAutocomplete
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedCompany(newValue.node.id);
                    setSelectedTableID({
                      tableID: newValue.node.id,
                      tableType: TableType.COMPANY,
                    });
                  } else {
                    setSelectedCompany(0);
                    setSelectedTableID({
                      tableID: customer_id!,
                      tableType: TableType.CUSTOMER,
                    });
                  }
                }}
                onKeyDown={() => {}}
                value={selectedCompany}
                label={companyLabel}
              />
            </div>
          )}
          {enableFields(TableType.SUBSIDIARY) && sub_id === undefined ? (
            <div className="col-2">
              <SubsidiarySelect
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedSubsidiary(newValue.node.id);
                    setSelectedTableID({
                      tableID: newValue.node.id,
                      tableType: TableType.SUBSIDIARY,
                    });
                  } else {
                    setSelectedSubsidiary(0);
                    setSelectedTableID({
                      tableID: customer_id!,
                      tableType: TableType.CUSTOMER,
                    });
                  }
                }}
                companyID={selectedCompany}
                onKeyDown={() => {}}
                value={selectedSubsidiary}
                label={subsidiaryLabel}
              />
            </div>
          ) : null}
          {enableFields(TableType.SITE) && siteID === undefined ? (
            <div className="col-2">
              <SiteSelect
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedSite(newValue.value);
                    setSelectedTableID({
                      tableID: newValue.value,
                      tableType: TableType.SITE,
                    });
                  } else {
                    setSelectedSite(0);
                    setSelectedTableID({
                      tableID: customer_id!,
                      tableType: TableType.CUSTOMER,
                    });
                  }
                }}
                companyId={selectedCompany}
                subsidiaryId={selectedSubsidiary}
                onKeyDown={() => {}}
                value={selectedSite}
                label={subsidiarySiteLabel}
              />
            </div>
          ) : null}
        </div>

        <div className="ast-sum-report__table--data">
          <TableContainer className="ast-sum-report__table--table-container">
            <MuiTable stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{companyLabel}</TableCell>
                  {enableFields(TableType.SUBSIDIARY) ? (
                    <TableCell>{subsidiaryLabel}</TableCell>
                  ) : null}
                  {enableFields(TableType.SITE) ? (
                    <TableCell>{subsidiarySiteLabel}</TableCell>
                  ) : null}
                  <TableCell>Total Asset</TableCell>
                  <TableCell>Working Asset Count</TableCell>
                  <TableCell>Tickets Raised Asset Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modifiedArrayForTable.map((data, i) => {
                  if (data) {
                    return (
                      <TableRow key={i}>
                        {data.company !== EMPTY_STRING ? (
                          <TableCell rowSpan={data.company_span}>
                            {data.company}
                          </TableCell>
                        ) : null}
                        {enableFields(TableType.SUBSIDIARY) &&
                        data.subsidiary !== EMPTY_STRING ? (
                          <TableCell rowSpan={data.subsidiary_span}>
                            {data.subsidiary}
                          </TableCell>
                        ) : null}
                        {enableFields(TableType.SITE) &&
                        data.sub_site !== EMPTY_STRING ? (
                          <TableCell rowSpan={data.sub_site_span}>
                            {data.sub_site}
                          </TableCell>
                        ) : null}
                        <TableCell id="td-center">{data.total_asset}</TableCell>
                        <TableCell id="td-center">{data.working}</TableCell>
                        <TableCell id="td-center">{data.not_working}</TableCell>
                      </TableRow>
                    );
                  }
                  return null;
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={colSpan} className="total">
                    Total
                  </TableCell>
                  <TableCell className="total-count">
                    {total.totalAssetCount}
                  </TableCell>
                  <TableCell className="total-count">
                    {total.workingAssetCount}
                  </TableCell>
                  <TableCell className="total-count">
                    {total.notWorkingAssetCount}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </MuiTable>
          </TableContainer>
        </div>

        <Button
          mode={setDetailAssets ? "cancel" : "back"}
          onClick={() => {
            if (setDetailAssets) setDetailAssets(false);
            else navigate(-1);
          }}
        />
      </div>

      <CustomModal
        style={AddModalStyles}
        isOpen={itemDetailsModal}
        onRequestClose={() => setItemDetails(!itemDetailsModal)}
        contentComponent={<ItemDetails setModalFlag={setItemDetails} />}
      />
    </>
  );
};

export default AssetSummaryTable;
